import { APP_TIMINGS_GROUP, getPerformance } from '@/perf/performanceApi'
import type { Timestamp } from '@amazon/vinyl-util'

/** DevTools lane every timed lazy import lands in, keyed by module name. */
export const MODULE_LOAD_LANE = 'Module load'

export interface TimingSpanOptions {
    readonly lane: string
    readonly label: string
    readonly color?: string
}

/**
 * Opens a timing span anchored at `now()` and returns a one-shot `done` that
 * draws a start→now bar in `lane` (under the shared app-timings group) and
 * returns the elapsed ms — so a caller can also emit a level-guarded log. The
 * span never throws into the instrumented caller.
 */
export function beginTimingSpan(options: TimingSpanOptions): () => number {
    const perf = getPerformance()
    const start: Timestamp = perf.now()
    let elapsed = 0
    let drawn = false
    return () => {
        if (drawn) return elapsed
        drawn = true
        const end = perf.now()
        elapsed = Math.max(0, end - start)
        perf.timeStamp({
            label: options.label,
            start,
            // Clamp against a backward wall-clock step (NTP) so the bar never reverses.
            end: Math.max(start, end),
            trackName: options.lane,
            trackGroup: APP_TIMINGS_GROUP,
            ...(options.color ? { color: options.color } : null),
        })
        return elapsed
    }
}

/**
 * Times a dynamic import, drawing a bar in the shared "Module load" lane and
 * returning the module. `onDone` reports the elapsed ms — for a level-guarded
 * caller log — for both a resolved and a rejected load (a failed lazy import is
 * still drawn and reported before the rejection re-throws).
 */
export async function timedModuleLoad<T>(
    name: string,
    load: () => Promise<T>,
    onDone?: (elapsedMs: number) => void
): Promise<T> {
    const done = beginTimingSpan({ lane: MODULE_LOAD_LANE, label: name, color: 'secondary' })
    try {
        return await load()
    } finally {
        onDone?.(done())
    }
}
